"use client";

import { useState } from "react";
import { Bookmark } from "lucide-react";
import { USER_STATUS_COLORS } from "@/shared/config";
import type { UserStatus } from "@/shared/ui/data-display/user-status";
import { cn } from "@/shared/lib/utils";
import { SAVED_AVATAR } from "../lib/to-conversation";

// KAN-1322: аватар дизайн-системы GoodLuck (gl-avatar): круг, инициалы фолбэком, точка «в сети»
// 28% диаметра с обводкой цветом фона. Размеры — как в макете чата (24/32/40/56).

interface ChatAvatarProps {
  name: string;
  src?: string | null;
  size: number;
  online?: boolean;
  status?: UserStatus;
  /** Цвет обводки точки присутствия — фон, на котором лежит аватар. */
  statusRingClassName?: string;
  className?: string;
}

function initialsOf(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w.charAt(0))
    .join("")
    .toUpperCase() || "?";
}

export function ChatAvatar({ name, src, size, online, status, statusRingClassName = "border-chat-bg", className }: ChatAvatarProps) {
  const [broken, setBroken] = useState(false);
  const showImage = !!src && !broken;
  return (
    <span
      className={cn("relative inline-flex flex-none items-center justify-center rounded-full", className)}
      style={{ width: size, height: size }}
    >
      {src === SAVED_AVATAR ? (
        <span aria-hidden className="flex size-full items-center justify-center rounded-full bg-chat-accent-bright text-chat-on-bright">
          <Bookmark style={{ width: size * 0.45, height: size * 0.45 }} strokeWidth={2.2} />
        </span>
      ) : showImage ? (
        // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- onError — событие загрузки картинки, не действие пользователя
        <img
          src={src}
          alt={name}
          className="size-full rounded-full bg-muted object-cover"
          onError={() => setBroken(true)}
        />
      ) : (
        <span
          aria-hidden
          className="flex size-full items-center justify-center rounded-full bg-muted font-heading font-bold uppercase text-muted-foreground"
          style={{ fontSize: Math.max(10, Math.round(size * 0.4)) }}
        >
          {initialsOf(name)}
        </span>
      )}
      {(status ? status !== "offline" && status !== "invisible" : online) && (
        <span
          aria-hidden
          className={cn("absolute right-0 bottom-0 z-[3] min-h-2 min-w-2 rounded-full border-2 shadow-[0_0_0_1px_var(--chat-border)]", statusRingClassName, status ? USER_STATUS_COLORS[status.toUpperCase() as keyof typeof USER_STATUS_COLORS] : "bg-chat-online")}
          style={{ width: "28%", height: "28%" }}
        />
      )}
    </span>
  );
}
